import { memo, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  ButtonBase,
  InputAdornment,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { designTokens } from '@design-system';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { activeItem, openDrawer } from '@/store/reducers/menu';
import { useShortcutContext } from '@/shortcuts/ShortcutProvider';
import { MENU_SEARCH_EVENT } from '@/shortcuts/actions';
import { getMenuItems } from '@/menu-items/queries';
import NavGroup from './NavGroup';
import NavItem from './NavItem';
import { useMenuFavourites } from './favourites';

type MenuNode = {
  id: string;
  url?: string;
  children?: MenuNode[];
  [key: string]: any;
};

const normalizePath = (path?: string) => {
  if (!path) {
    return '';
  }

  let cleanPath = path.trim().toLowerCase().split(/[?#]/)[0];
  if (!cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  return cleanPath.length > 1 ? cleanPath.replace(/\/+$/, '') : cleanPath;
};

const isRouteMatch = (pathname: string, itemUrl?: string) => {
  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(itemUrl);

  if (!targetPath) {
    return false;
  }

  // Direct exact match or sub-path match
  if (currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)) {
    return true;
  }

  // Handle underscore vs hyphen differences (e.g., payroll_period vs payroll-period)
  const currentClean = currentPath.replace(/_/g, '-');
  const targetClean = targetPath.replace(/_/g, '-');
  if (
    currentClean === targetClean ||
    currentClean.startsWith(`${targetClean}/`)
  ) {
    return true;
  }

  // Handle prefix variations (e.g. /payroll-period vs /hr/payroll-period)
  if (
    targetClean.length > 1 &&
    (currentClean.endsWith(targetClean) || targetClean.endsWith(currentClean))
  ) {
    return true;
  }

  return false;
};

const findActiveItemId = (items: MenuNode[] = [], pathname: string) => {
  let activeItemId = '';
  let activePathLength = -1;

  const visit = (item: MenuNode) => {
    const itemPath = normalizePath(item.url);

    if (
      isRouteMatch(pathname, item.url) &&
      itemPath.length > activePathLength
    ) {
      activeItemId = item.id;
      activePathLength = itemPath.length;
    }

    item.children?.forEach(visit);
  };

  items.forEach(visit);

  return activeItemId;
};

const hasActiveChild = (item: MenuNode, activeItemId: string): boolean => {
  if (!activeItemId) {
    return false;
  }

  if (item?.id === activeItemId) {
    return true;
  }

  return Boolean(
    item?.children?.some((child) => hasActiveChild(child, activeItemId))
  );
};

const findMenuPath = (
  items: MenuNode[] = [],
  targetId: string,
  path: string[] = []
): string[] => {
  for (const item of items) {
    const nextPath = [...path, item.id];

    if (item.id === targetId) {
      return nextPath;
    }

    const childPath = findMenuPath(item.children, targetId, nextPath);

    if (childPath.length) {
      return childPath;
    }
  }

  return [];
};

/**
 * Items whose title, together with the titles of the sections above it,
 * contains every word typed (any order): "stock age" finds MIS Reports >
 * Stock Ageing.
 */
const filterMenuItems = (items: MenuNode[] = [], searchValue: string, parents = ''): MenuNode[] => {
  const words = searchValue.trim().toLowerCase().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return items;
  }

  return items.reduce<MenuNode[]>((filteredItems, item) => {
    const path = `${parents} ${item.title ?? ''}`.toLowerCase();
    const leaf = !item.children?.length;

    if (leaf && words.every((w) => path.includes(w))) {
      filteredItems.push(item);
      return filteredItems;
    }

    const children = filterMenuItems(item.children, searchValue, path);

    if (children.length) {
      filteredItems.push({
        ...item,
        children,
      });
    }

    return filteredItems;
  }, []);
};

/** first page (leaf) in menu order */
const firstLeaf = (items: MenuNode[] = []): MenuNode | null => {
  for (const item of items) {
    if (!item.children?.length && item.url) return item;
    const found = firstLeaf(item.children);
    if (found) return found;
  }
  return null;
};

/* ------------------------------------------------- recent pages (per user) */

type Recent = { id: string; title: string; url: string; section: string };
const RECENT_MAX = 8;
const recentKey = (user?: string | null) => `erp.menuRecent.${user ?? ''}`;
const readRecent = (user?: string | null): Recent[] => {
  try {
    const v = JSON.parse(localStorage.getItem(recentKey(user)) ?? '[]');
    return Array.isArray(v) ? v.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
};
const writeRecent = (user: string | null | undefined, list: Recent[]) => {
  try {
    localStorage.setItem(recentKey(user), JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* private window / storage blocked: recents just are not kept */
  }
};

const getCollapseIds = (items: MenuNode[] = []) =>
  items.reduce<string[]>((ids, item) => {
    if (item.children?.length) {
      ids.push(item.id, ...getCollapseIds(item.children));
    }

    return ids;
  }, []);

const Navigation = memo(function Navigation() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loggedUserData } = useAppSelector((state) => state.loginReducer);
  const { openItem, drawerOpen } = useAppSelector((state) => state.menu);
  const favs = useMenuFavourites();
  const { keymap } = useShortcutContext();
  const { data: menuItems, refetch } = useQuery({
    queryKey: ['todos'],
    queryFn: () => getMenuItems(),
    enabled: !!loggedUserData?.comp_code,
  });

  const [openMenus, setOpenMenus] = useState<string[] | null>(null);
  const openMenusRef = useRef<string[] | null>(null);
  openMenusRef.current = openMenus;
  const [searchValue, setSearchValue] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [recent, setRecent] = useState<Recent[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const user = loggedUserData?.user_code;

  useEffect(() => setRecent(readRecent(user)), [user]);

  /** every menu node by id */
  const byId = useMemo(() => {
    const map = new Map<string, MenuNode>();
    const visit = (list: MenuNode[] = []) =>
      list.forEach((m) => {
        map.set(m.id, m);
        visit(m.children);
      });
    visit(menuItems ?? []);
    return map;
  }, [menuItems]);

  /** "Finance › Receivables": the sections above a page */
  const sectionOf = useCallback(
    (id: string) =>
      findMenuPath(menuItems ?? [], id)
        .slice(0, -1)
        .map((x) => byId.get(x)?.title ?? '')
        .filter(Boolean)
        .join(' › '),
    [menuItems, byId]
  );

  /** favourites still in this user's menu, in the user's order */
  const favourites = useMemo(
    () =>
      favs.ids
        .map((id) => byId.get(id))
        .filter((m): m is MenuNode => !!m && !!m.url && !m.children?.length)
        .map((m) => ({ id: m.id, title: String(m.title ?? ''), url: String(m.url), section: sectionOf(m.id) })),
    [favs.ids, byId, sectionOf]
  );

  /** remember a page opened from the menu (most recent first) */
  const remember = useCallback(
    (item: MenuNode) => {
      if (!item?.url || item.children?.length) return;
      const section = sectionOf(item.id);
      const next = [
        { id: item.id, title: String(item.title ?? ''), url: item.url, section },
        ...readRecent(user).filter((r) => r.id !== item.id),
      ].slice(0, RECENT_MAX);
      writeRecent(user, next);
      setRecent(next);
    },
    [sectionOf, user]
  );

  const handleToggle = useCallback(
    (key: string, items: any) => {
      if (!items.children?.length) {
        remember(items);
        setOpenMenus(findMenuPath(menuItems ?? [], key).slice(0, -1));
        return;
      }

      const opening = !openMenusRef.current?.includes(key);
      setOpenMenus((prev) => {
        if (prev?.includes(key)) {
          return prev.slice(0, prev.indexOf(key));
        }

        const menuPath = findMenuPath(menuItems ?? [], key);
        return menuPath;
      });
      // opening a section closes the others, so it can jump: bring it to the top of the list
      if (opening) {
        setTimeout(() => {
          const el = listRef.current?.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(key)}"]`);
          el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 320);
      }
    },
    [menuItems, remember]
  );

  useEffect(() => {
    if (loggedUserData?.comp_code) {
      refetch();
    }
  }, [loggedUserData?.comp_code, refetch]);

  useEffect(() => {
    setOpenMenus(null);
  }, [pathname]);

  // Menu Search shortcut (Ctrl+M by default): open the drawer, focus the search
  useEffect(() => {
    const focusSearch = () => {
      dispatch(openDrawer({ drawerOpen: true }));
      // after the drawer has opened
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 60);
    };
    window.addEventListener(MENU_SEARCH_EVENT, focusSearch);
    return () => window.removeEventListener(MENU_SEARCH_EVENT, focusSearch);
  }, [dispatch]);

  // the menu is loaded once at sign-in: a page added since then (or newly granted) is not in it, and the nearest
  // parent page would be highlighted instead. Opening a page the menu does not list reloads the menu (at most once
  // a minute), so the new entry appears and is highlighted.
  const menuReloadAt = useRef(0);
  useEffect(() => {
    if (!menuItems?.length) return;
    const here = normalizePath(pathname);
    let listed = false;
    const visit = (list: MenuNode[] = []) =>
      list.forEach((m) => {
        if (!listed && m.url && normalizePath(m.url) === here) listed = true;
        if (!listed) visit(m.children);
      });
    visit(menuItems);
    if (listed || Date.now() - menuReloadAt.current < 60_000) return;
    menuReloadAt.current = Date.now();
    refetch();
  }, [pathname, menuItems, refetch]);

  const activeItemId = useMemo(() => {
    const fromPath = findActiveItemId(menuItems ?? [], pathname);
    if (fromPath) {
      return fromPath;
    }

    if (openItem && openItem.length > 0) {
      const foundPath = findMenuPath(menuItems ?? [], openItem[0]);
      if (foundPath.length > 0) {
        return openItem[0];
      }
    }

    return '';
  }, [menuItems, pathname, openItem]);

  // keep the page you are on in view (after a reload it can be far down the list)
  useEffect(() => {
    if (!activeItemId || searchValue.trim()) return;
    const t = setTimeout(() => {
      const el = listRef.current?.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(activeItemId)}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }, 250);
    return () => clearTimeout(t);
  }, [activeItemId, menuItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // new search text: show the results from the top
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [searchValue]);

  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems ?? [], searchValue),
    [menuItems, searchValue]
  );

  const searchOpenMenus = useMemo(
    () => getCollapseIds(filteredMenuItems),
    [filteredMenuItems]
  );

  const navGroups = useMemo(
    () =>
      filteredMenuItems?.map((item: any) => {
        const isOpen = (menuItem: MenuNode) =>
          searchValue.trim()
            ? searchOpenMenus.includes(menuItem.id)
            : openMenus
              ? openMenus.includes(menuItem.id)
              : hasActiveChild(menuItem, activeItemId);

        switch (item.type) {
          case 'collapse':
            return (
              <NavGroup
                key={item.id}
                item={item}
                isChildOpen={isOpen(item)}
                isMenuOpen={isOpen}
                activeItemId={activeItemId}
                onToggle={handleToggle}
              />
            );
          case 'item':
            return (
              <NavItem
                key={item.id}
                item={item}
                level={1}
                activeItemId={activeItemId}
                onToggle={handleToggle}
              />
            );
          default:
            return null;
        }
      }),
    [
      filteredMenuItems,
      searchValue,
      searchOpenMenus,
      openMenus,
      activeItemId,
      handleToggle,
    ]
  );

  /* ------------------------------------------------- keyboard navigation */

  /** menu entries (and recent pages) on screen, in order */
  const entries = () =>
    [...(rootRef.current?.querySelectorAll<HTMLElement>('[data-nav-id]') ?? [])].filter((el) => el.offsetParent !== null);

  const focusEntry = (index: number) => {
    const list = entries();
    if (!list.length) return;
    const el = list[Math.max(0, Math.min(index, list.length - 1))];
    el.focus();
    el.scrollIntoView({ block: 'nearest' });
  };

  const openPage = (item: { id: string; url: string; title?: string; children?: MenuNode[] }) => {
    remember(item as MenuNode);
    dispatch(activeItem({ openItem: [item.id] }));
    setSearchValue('');
    navigate(item.url);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusEntry(0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = searchValue.trim() ? firstLeaf(filteredMenuItems) : recent[0] ?? null;
      if (target?.url) openPage(target as any);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (searchValue) setSearchValue('');
      else inputRef.current?.blur();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target === inputRef.current) return;
    const list = entries();
    const index = list.indexOf(target);
    if (index < 0) return;
    const kind = target.dataset.navKind;
    const level = Number(target.dataset.navLevel ?? 1);
    const open = target.dataset.navOpen === 'Y';
    const stop = () => {
      e.preventDefault();
      e.stopPropagation();
    };

    const navId = target.dataset.navId ?? '';
    const pageId = navId.startsWith('fav:') ? navId.slice(4) : kind === 'item' ? navId : '';
    if (e.key === '*' && pageId) {
      stop();
      favs.toggle(pageId);
      return;
    }
    if (e.altKey && navId.startsWith('fav:') && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      stop();
      favs.move(pageId, e.key === 'ArrowUp' ? -1 : 1);
      setTimeout(() => rootRef.current?.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(navId)}"]`)?.focus(), 0);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        stop();
        focusEntry(index + 1);
        break;
      case 'ArrowUp':
        stop();
        if (index === 0) inputRef.current?.focus();
        else focusEntry(index - 1);
        break;
      case 'Home':
        stop();
        focusEntry(0);
        break;
      case 'End':
        stop();
        focusEntry(list.length - 1);
        break;
      case 'ArrowRight':
        if (kind !== 'group') break;
        stop();
        if (!open) target.click();
        else focusEntry(index + 1);
        break;
      case 'ArrowLeft': {
        stop();
        if (kind === 'group' && open) {
          target.click();
          break;
        }
        // up to the section this entry is in
        for (let i = index - 1; i >= 0; i -= 1) {
          if (list[i].dataset.navKind === 'group' && Number(list[i].dataset.navLevel ?? 1) < level) {
            list[i].focus();
            break;
          }
        }
        break;
      }
      case 'Escape':
        stop();
        inputRef.current?.focus();
        break;
      default:
        // typing a letter goes on in the search box
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
          stop();
          setSearchValue((v) => v + e.key);
          inputRef.current?.focus();
        }
    }
  };

  const shortcut = keymap?.MENU_SEARCH ?? null;
  const showRecent = searchActive && !searchValue.trim() && recent.length > 0;

  return (
    <Box
      ref={rootRef}
      onKeyDown={onMenuKeyDown}
      sx={{
        pt: 1,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={searchBoxRef}
        onFocus={() => setSearchActive(true)}
        onBlur={(e) => {
          if (!searchBoxRef.current?.contains(e.relatedTarget as Node)) setSearchActive(false);
        }}
        sx={{ px: 0.75, pb: 1, flexShrink: 0 }}
      >
        <TextField
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onKeyDown={onSearchKeyDown}
          inputRef={inputRef}
          placeholder={shortcut ? `Search menu (${shortcut})` : 'Search menu'}
          size="small"
          fullWidth
          name="menu-search"
          id="menu-search-input"
          autoComplete="off"
          inputProps={{
            readOnly: true,
            onFocus: (e) => e.target.removeAttribute('readonly'),
            'aria-label': 'Search menu',
          }}
          type="search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                  sx={{
                    color: designTokens.colors.neutral[500],
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(designTokens.colors.neutral[500], 0.08),
              color: designTokens.colors.neutral[100],
              fontFamily: 'inherit',
              border: `1px solid ${alpha(designTokens.colors.neutral[500], 0.16)}`,
              transition: `all ${designTokens.transitions.duration.fast}`,
              '&:hover': {
                bgcolor: alpha(designTokens.colors.neutral[500], 0.12),
                borderColor: alpha(designTokens.colors.primary[500], 0.25),
              },
              '&.Mui-focused': {
                borderColor: alpha(designTokens.colors.primary[500], 0.45),
                boxShadow: `0 0 0 3px ${alpha(designTokens.colors.primary[500], 0.12)}`,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& input': {
              p: 0,
              '&::placeholder': {
                color: designTokens.colors.neutral[500],
                opacity: 1,
              },
            },
          }}
        />

        {showRecent && (
          <Box sx={{ mt: 1, px: 0.5, maxHeight: '40vh', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.75, mb: 0.25 }}>
              <Typography sx={{ fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: designTokens.colors.neutral[500] }}>
                Recent
              </Typography>
              <ButtonBase
                onClick={() => {
                  writeRecent(user, []);
                  setRecent([]);
                  inputRef.current?.focus();
                }}
                sx={{ fontSize: '0.68rem', color: designTokens.colors.neutral[500], px: 0.5, borderRadius: 1, '&:hover, &:focus-visible': { color: designTokens.colors.neutral[100] } }}
              >
                Clear
              </ButtonBase>
            </Box>
            {recent.map((r) => (
              <ButtonBase
                key={r.id}
                data-nav-id={`recent:${r.id}`}
                data-nav-kind="recent"
                data-nav-level={1}
                onClick={() => openPage(r)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 1,
                  textAlign: 'left',
                  px: 0.75,
                  py: 0.6,
                  borderRadius: '8px',
                  color: designTokens.colors.neutral[200],
                  '&:hover, &:focus-visible': { bgcolor: alpha(designTokens.colors.primary[500], 0.14), color: '#fff' },
                }}
              >
                <HistoryRoundedIcon sx={{ fontSize: 16, mt: '2px', color: designTokens.colors.neutral[500] }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.8rem', fontFamily: 'inherit' }}>
                    {r.title}
                  </Typography>
                  {r.section && (
                    <Typography noWrap sx={{ fontSize: '0.68rem', color: designTokens.colors.neutral[500], fontFamily: 'inherit' }}>
                      {r.section}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>

      {/* only the menu list scrolls; the search box above stays in place */}
      <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 1 }}>
        {searchValue.trim() && !filteredMenuItems.length ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: designTokens.colors.neutral[500],
                fontFamily: 'inherit',
                textAlign: 'center',
              }}
            >
              No menu found
            </Typography>
          </Box>
        ) : (
          <>
            {drawerOpen && !searchValue.trim() && favourites.length > 0 && (
              <Box sx={{ px: 0.75, pb: 1, mb: 1, borderBottom: `1px solid ${alpha(designTokens.colors.neutral[500], 0.12)}` }}>
                <Typography
                  sx={{ fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: designTokens.colors.neutral[500], px: 0.75, mb: 0.25 }}
                >
                  Favourites
                </Typography>
                {favourites.map((f) => {
                  const active = f.id === activeItemId;
                  return (
                    <ButtonBase
                      key={f.id}
                      data-nav-id={`fav:${f.id}`}
                      data-nav-kind="fav"
                      data-nav-level={1}
                      onClick={() => openPage(f)}
                      title={`${f.section ? f.section + ' › ' : ''}${f.title}  (* removes, Alt+↑/↓ moves)`}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1,
                        textAlign: 'left',
                        px: 0.75,
                        py: 0.6,
                        borderRadius: '8px',
                        color: active ? '#fff' : designTokens.colors.neutral[200],
                        bgcolor: active ? alpha(designTokens.colors.primary[500], 0.15) : 'transparent',
                        '&:hover, &:focus-visible': { bgcolor: alpha(designTokens.colors.primary[500], 0.14), color: '#fff' },
                        '&:focus-visible': { outline: `2px solid ${designTokens.colors.primary[400]}`, outlineOffset: '-2px' },
                      }}
                    >
                      <Box
                        component="span"
                        role="button"
                        aria-label={`Remove ${f.title} from favourites`}
                        title={`Remove ${f.title} from favourites`}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          favs.toggle(f.id);
                        }}
                        sx={{ display: 'inline-flex', color: '#fbbf24', '&:hover': { color: designTokens.colors.neutral[400] } }}
                      >
                        <StarRoundedIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.8rem', fontFamily: 'inherit', fontWeight: active ? 600 : 400 }}>
                          {f.title}
                        </Typography>
                        {f.section && (
                          <Typography noWrap sx={{ fontSize: '0.68rem', color: designTokens.colors.neutral[500], fontFamily: 'inherit' }}>
                            {f.section}
                          </Typography>
                        )}
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            )}
            {navGroups}
          </>
        )}
      </Box>
    </Box>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;
